import { TALLINN } from '../../src/wire';

/**
 * Display formatting — the one place cents, counts and `YYYY-MM-DD` become Estonian text.
 *
 * The API deliberately ships raw units (integer cents, ISO dates, plain integers) so the server
 * never has to know what a reader speaks (`routes.ts`). This module is the other half of that
 * bargain: if a euro figure is rendered anywhere in the frontend, it is rendered here, so the
 * thousands separator, the decimal comma and the position of the `€` are decided once.
 *
 * All of it is `Intl` against `et-EE`, not hand-rolled string surgery — Estonian groups thousands
 * with a non-breaking space and separates decimals with a comma, and both of those are locale data
 * rather than facts worth re-deriving in application code.
 */

const NBSP = ' ';

const GROUPED = new Intl.NumberFormat('et-EE', { maximumFractionDigits: 0 });

const LONG_DATE = new Intl.DateTimeFormat('et-EE', { dateStyle: 'long', timeZone: 'UTC' });

/** Built once, at module scope: constructing an `Intl` formatter costs far more than formatting
 * with it. See {@link todayInTallinn} for why the zone is not the reader's. */
const TALLINN_DAY = new Intl.DateTimeFormat('sv-SE', { timeZone: TALLINN });

/**
 * A whole number with Estonian thousands grouping: `12345` → `12 345` (non-breaking space).
 *
 * Grouping starts at five digits, not four: Estonian locale data sets `minimumGroupingDigits: 2`,
 * so `1234` renders as `1234`. That is `et-EE`'s rule, not a rounding of ours — see
 * `tests/web-format.test.ts`, which pins both sides of the threshold.
 */
export function formatCount(n: number): string {
  return GROUPED.format(n);
}

/**
 * Integer cents as an Estonian euro amount: `41290000` → `412 900 €`.
 *
 * Cents are only spelled out when they are non-zero. Every damage figure in the corpus that came
 * from a phrase like `82 871,24 eurot` keeps its cents; the far more common round `33 600 eurot`
 * is not padded with a meaningless `,00` that makes a police estimate look surveyed.
 *
 * The integer part is grouped separately from the fraction rather than dividing by 100 first, so a
 * large total can never pick up a floating-point artefact on its way to the screen. The space
 * before `€` is non-breaking: an amount that wraps between its digits and its unit is unreadable.
 */
export function formatEuros(cents: number): string {
  const rounded = Math.round(cents);
  const sign = rounded < 0 ? '−' : '';
  const abs = Math.abs(rounded);
  const whole = Math.trunc(abs / 100);
  const fraction = abs % 100;
  const digits =
    fraction === 0
      ? GROUPED.format(whole)
      : `${GROUPED.format(whole)},${String(fraction).padStart(2, '0')}`;
  return `${sign}${digits}${NBSP}€`;
}

/**
 * The denominator that has to accompany every damage total (spec §7).
 *
 * A euro sum over a corpus of press releases covers only the incidents whose text stated a figure,
 * which is a minority of them. `412 900 €` alone therefore implies a completeness the data does
 * not have; `412 900 € · 38 juhtumit summaga` says exactly what was counted. `StatBadge` refuses
 * to render a damage badge without one, and this is the string it expects.
 */
export function damageDenominator(count: number): string {
  return count === 1 ? '1 juhtum summaga' : `${formatCount(count)} juhtumit summaga`;
}

/** `2026-08-11` → `11. august 2026`. Anything not a plain date is shown verbatim, never as `NaN`. */
export function formatDate(ymd: string): string {
  const at = new Date(`${ymd}T00:00:00Z`);
  if (Number.isNaN(at.getTime())) return ymd;
  return LONG_DATE.format(at);
}

const RATE = new Intl.NumberFormat('et-EE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** A per-capita rate in Estonian: `0.72` is not a number here, `0,72` is. */
export function formatRate(n: number): string {
  return RATE.format(n);
}

/** `2026-08-30` → `30.08`: the short form the counter captions and the chronicle's day pill carry,
 * where the year is carried by the caption or panel around it — a full date would not fit a caption
 * that already names two of them. Anything not a plain date is shown verbatim, never as `NaN`. */
export function formatDayMonth(ymd: string): string {
  const [, month, day] = ymd.split('-');
  return month === undefined || day === undefined ? ymd : `${day}.${month}`;
}

/** `2026-08-30` → `30.08.2026`: the numeric form a filter trigger and a chip wear, where the long
 * date would not fit. Anything not a plain date is shown verbatim, never as `NaN`. */
export function formatShortDate(ymd: string): string {
  const [year, month, day] = ymd.split('-');
  return year === undefined || month === undefined || day === undefined
    ? ymd
    : `${day}.${month}.${year}`;
}

/**
 * Today in Europe/Tallinn as `YYYY-MM-DD`.
 *
 * The date-range presets mean "this year" and "the last twelve months" in the reader's calendar,
 * not in UTC's — and between midnight and 03:00 local time those are different years. `sv-SE` is
 * used purely because its date format *is* ISO 8601, which makes the conversion one call instead
 * of three `getUTC*` reads against a shifted clock.
 *
 * `now` is the project's injected-clock convention (`src/api/cache.ts`): a zero-argument reader
 * defaulting to `Date.now`, so every call site stays unchanged and a test can name an instant.
 */
export function todayInTallinn(now: () => number = Date.now): string {
  return TALLINN_DAY.format(new Date(now()));
}

/** `days` before a `YYYY-MM-DD`, in UTC — the arithmetic every date on this site is done in. */
export function daysBefore(ymd: string, days: number): string {
  return new Date(Date.parse(`${ymd}T00:00:00Z`) - days * 86_400_000).toISOString().slice(0, 10);
}

/**
 * The same calendar day `months` earlier, clamped to the end of the target month.
 *
 * Naive month arithmetic rolls 31 March back to 3 March (February has no 31st); a date-range
 * preset that silently moved the window by three days would be a quiet lie about what is on the
 * chart. Clamping to the last day the month has is the behaviour every calendar picker uses.
 */
export function monthsBefore(ymd: string, months: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  if (y === undefined || m === undefined || d === undefined) return ymd;
  const targetMonth = m - 1 - months; // zero-based, may go negative — Date.UTC normalises it
  const lastDay = new Date(Date.UTC(y, targetMonth + 1, 0)).getUTCDate();
  return new Date(Date.UTC(y, targetMonth, Math.min(d, lastDay))).toISOString().slice(0, 10);
}
